import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { PrimaryCTA, SoftCTA } from "@/components/CTA";
import { CircleScribble } from "@/components/Annotation";
import { Spotlight } from "@/components/Sketches";
import { LINKS, ROUTES } from "@/config/links";
import { useLang } from "@/i18n";

export const FinalCTA = () => {
  const { t } = useLang();
  return (
    <section className="relative z-10 py-28 md:py-44 bg-ink text-paper overflow-hidden" data-testid="final-cta-section">
      <span className="pointer-events-none select-none absolute inset-0 flex items-center justify-center font-hand text-[30vw] text-paper/[0.04] leading-none">
        take one
      </span>
      <Spotlight className="hidden md:block absolute left-6 lg:left-24 top-1/2 -translate-y-1/2 w-56 h-[380px] opacity-35 pointer-events-none" animate={false} label={false} invert />
      <Spotlight className="hidden md:block absolute right-6 lg:right-24 top-1/2 -translate-y-1/2 w-56 h-[380px] opacity-35 pointer-events-none scale-x-[-1]" animate={false} label={false} invert />

      <div className="max-w-[1000px] mx-auto px-5 md:px-10 relative text-center">
        <Reveal>
          <p className="font-body text-xs uppercase tracking-[0.28em] text-terracotta mb-8">
            {t("landing.final_eyebrow")}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight mb-10">
            <span className="relative inline-block">
              {t("landing.final_h")}
              <CircleScribble className="absolute -left-6 -top-4 w-[130%] h-[170%]" delay={0.4} />
            </span>
          </h2>
        </Reveal>

        <Reveal delay={0.2}>
          <p className="hidden md:block font-body text-lg text-paper/70 max-w-xl mx-auto mb-12 leading-relaxed" data-testid="final-body-desktop">
            {t("landing.final_body")}
          </p>
          <p className="md:hidden font-body text-paper/70 max-w-xl mx-auto mb-10 leading-relaxed" data-testid="final-body-mobile">
            {t("landing.final_body_mobile")}
          </p>
        </Reveal>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="flex flex-col items-center gap-4"
        >
          <PrimaryCTA href={ROUTES.bookPresentation} testId="final-book-cta" location="final" targetAssetId="booking_presentation">
            {t("landing.final_book_cta")}
          </PrimaryCTA>
          <p className="font-body text-sm text-paper/60">{t("landing.final_microcopy")}</p>
        </motion.div>

        <Reveal delay={0.4} className="mt-10">
          <p className="hidden md:block font-body text-sm text-paper/60 mb-2" data-testid="final-secondary-prompt-desktop">
            {t("landing.final_secondary_prompt")}
          </p>
          <p className="md:hidden font-body text-sm text-paper/60 mb-2" data-testid="final-secondary-prompt-mobile">
            {t("landing.final_secondary_prompt_mobile")}
          </p>
          <SoftCTA href={LINKS.diagnostic} testId="final-diagnostic-cta" location="final" tone="dark" targetAssetId="next_presentation_readiness">
            {t("landing.final_diagnostic_cta")}
          </SoftCTA>
        </Reveal>
      </div>
    </section>
  );
};
